import React from 'react';
import { Link } from 'react-router-dom';
import documentService from '../../../services/documentService';
import classroomService from '../../../services/classroomService';
import types from '../../../services/types';
import sanitizeHtml from '../../../utils/sanitizeHtml';

// Piezas visuales compartidas entre la vista del estudiante (ClassroomView)
// y el panel del docente (ClassroomManage), para que ambas se vean
// exactamente igual — el docente solo suma los botones de administración
// (visible/oculto, editar, eliminar, agregar), nunca cambia el contenido.

export const buildEmbedUrl = (providerId, videoUrl) => {
    try {
        if (providerId === types.classroomVideoProvider.YOUTUBE) {
            const url = new URL(videoUrl);
            const id = url.hostname.includes('youtu.be') ? url.pathname.slice(1) : url.searchParams.get('v');
            return id ? `https://www.youtube.com/embed/${id}` : null;
        }
        if (providerId === types.classroomVideoProvider.VIMEO) {
            const url = new URL(videoUrl);
            const id = url.pathname.split('/').filter(Boolean).pop();
            return id ? `https://player.vimeo.com/video/${id}` : null;
        }
    } catch {
        return null;
    }
    return null;
};

// Trae document/video solo para los recursos indicados (no para toda el
// aula) — se usa de forma perezosa, solo con los recursos del intervalo
// que se está mostrando en pantalla, para no repetir el problema de
// peticiones en cascada que ya se corrigió en el resto del módulo.
export const enrichResources = async (resources) => Promise.all(
    (resources || []).map(async (r) => {
        if (r.resourceTypeId === types.classroomResourceTypes.VIDEO && r.videoId) {
            try {
                const video = await classroomService.getVideo(r.videoId);
                return { ...r, video };
            } catch { return r; }
        }
        if (r.resourceTypeId === types.classroomResourceTypes.FILE && r.documentId) {
            try {
                const document = await documentService.getDocumentById(r.documentId);
                return { ...r, document };
            } catch { return r; }
        }
        return r;
    })
);

// Contenido de un recurso según su tipo — idéntico para docente y
// estudiante, es la parte que "se ve igual".
const ResourceContent = ({ resource }) => {
    const nombre = resource.displayName || resource.name;

    if (resource.resourceTypeId === types.classroomResourceTypes.CONTENT) {
        return (
            <div>
                <strong>{nombre}</strong>
                <div className="mt-2" dangerouslySetInnerHTML={{ __html: sanitizeHtml(resource.contentHtml) }} />
            </div>
        );
    }

    if (resource.resourceTypeId === types.classroomResourceTypes.FILE) {
        return (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span><i className="fas fa-file me-2 text-muted"></i>{nombre}</span>
                {resource.document?.url && (
                    <a href={resource.document.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                        Descargar
                    </a>
                )}
            </div>
        );
    }

    if (resource.resourceTypeId === types.classroomResourceTypes.VIDEO && resource.video) {
        const embedUrl = buildEmbedUrl(resource.video.providerId, resource.video.videoUrl);
        return (
            <div>
                <strong>{nombre}</strong>
                {embedUrl ? (
                    <div className="ratio ratio-16x9 mt-2">
                        <iframe src={embedUrl} title={nombre} allowFullScreen></iframe>
                    </div>
                ) : (
                    <div className="mt-2">
                        <a href={resource.video.videoUrl} target="_blank" rel="noreferrer">Ver video <i className="fas fa-arrow-up-right-from-square ms-1"></i></a>
                    </div>
                )}
            </div>
        );
    }

    if (resource.resourceTypeId === types.classroomResourceTypes.FORUM) {
        return (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span><i className="fas fa-comments me-2 text-muted"></i>{nombre}</span>
                <Link to={`/aula/foro/${resource.forumId}`} className="btn btn-sm btn-outline-primary">Ir al foro</Link>
            </div>
        );
    }

    if (resource.resourceTypeId === types.classroomResourceTypes.TASK) {
        return (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span><i className="fas fa-list-check me-2 text-muted"></i>{nombre}</span>
                <Link to={`/aula/tarea/${resource.taskId}`} className="btn btn-sm btn-primary">Ver tarea</Link>
            </div>
        );
    }

    if (resource.resourceTypeId === types.classroomResourceTypes.EXAM) {
        return (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span><i className="fas fa-clock me-2 text-muted"></i>{nombre}</span>
                <Link to={`/aula/examen/${resource.examId}`} className="btn btn-sm btn-primary">Ver examen</Link>
            </div>
        );
    }

    if (resource.resourceTypeId === types.classroomResourceTypes.LINK) {
        return (
            <a href={resource.externalUrl} target="_blank" rel="noreferrer">
                <i className="fas fa-link me-2"></i>{nombre} <i className="fas fa-arrow-up-right-from-square ms-1"></i>
            </a>
        );
    }

    return <span>{nombre}</span>;
};

// admin (opcional): { pending, onToggleVisible, onEdit, onDelete }
// Sin admin -> vista de estudiante, tal cual. Con admin -> mismo
// contenido + barra de acciones (el "ver tarea/examen/foro" de arriba ya
// sirve como acceso a gestionar, esas pantallas se adaptan solas por rol).
export const ResourceItem = ({ resource, admin }) => {
    if (!admin) return <ResourceContent resource={resource} />;

    return (
        <div className={!resource.isVisible ? 'opacity-50' : ''}>
            <div className="d-flex justify-content-between align-items-start gap-2">
                <div className="flex-grow-1">
                    <ResourceContent resource={resource} />
                </div>
                <div className="btn-group btn-group-sm flex-shrink-0">
                    <button className="btn btn-outline-secondary" onClick={admin.onToggleVisible} disabled={admin.pending} title="Mostrar/ocultar">
                        <i className={`fas ${admin.pending ? 'fa-spinner fa-spin' : resource.isVisible ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                    <button className="btn btn-outline-info" onClick={admin.onEdit} title="Editar colocación">
                        <i className="fas fa-pencil-alt"></i>
                    </button>
                    <button className="btn btn-outline-danger" onClick={admin.onDelete} title="Quitar">
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            </div>
            {!resource.isVisible && <span className="badge bg-secondary mt-1">Oculto para estudiantes</span>}
        </div>
    );
};

// admin (opcional): { pending, onToggleVisible, onEdit, onDelete, onAddResource, resourceAdmin: (resource) => adminProps }
export const SectionCard = ({ section, admin }) => (
    <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
                {section.icon && <span className="me-1">{section.icon}</span>}
                <strong>{section.name}</strong>
                {admin && (
                    <span className={`badge ms-2 ${section.isVisible ? 'bg-success' : 'bg-secondary'}`}>
                        {section.isVisible ? 'Visible' : 'Oculto'}
                    </span>
                )}
            </div>
            {admin && (
                <div className="btn-group btn-group-sm">
                    <button className="btn btn-outline-secondary" onClick={admin.onToggleVisible} disabled={admin.pending} title="Mostrar/ocultar">
                        <i className={`fas ${admin.pending ? 'fa-spinner fa-spin' : section.isVisible ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                    <button className="btn btn-outline-info" onClick={admin.onEdit} title="Editar">
                        <i className="fas fa-pencil-alt"></i>
                    </button>
                    <button className="btn btn-outline-danger" onClick={admin.onDelete} title="Eliminar">
                        <i className="fas fa-times"></i>
                    </button>
                    <button className="btn btn-outline-primary" onClick={admin.onAddResource} title="Agregar recurso">
                        <i className="fas fa-plus"></i> Recurso
                    </button>
                </div>
            )}
        </div>
        <div className="card-body">
            {section.descriptionHtml && (
                <div className="mb-3" dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.descriptionHtml) }} />
            )}
            {section.resources.length === 0 ? (
                <p className="text-muted mb-0">Sin recursos todavía.</p>
            ) : (
                <div className="list-group list-group-flush">
                    {section.resources.map((r) => (
                        <div className="list-group-item" key={r.resourceId}>
                            <ResourceItem resource={r} admin={admin && admin.resourceAdmin(r)} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    </div>
);

// admin (opcional): true para mostrar el ícono de oculto en cada botón
export const IntervalSelector = ({ intervals, seleccionado, onSelect, admin }) => (
    <div className="mb-4">
        <h2 className="h6 text-uppercase text-muted">Intervalos</h2>
        <div className="d-flex flex-wrap gap-2">
            {intervals.map((iv) => (
                <button
                    key={iv.intervalId}
                    type="button"
                    className={`btn av-week-btn ${iv.intervalId === seleccionado?.intervalId ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => onSelect(iv)}
                >
                    {admin && !iv.isVisible && <i className="fas fa-eye-slash me-1" title="Oculto para estudiantes"></i>}
                    {iv.name}
                </button>
            ))}
        </div>
    </div>
);
